import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from '../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import '../component_styles/searchbar.css'
import { optionsAlbums, optionsCollections, optionsTags, customStylesAlbums, customStylesCards, customStylesCollections, customStylesTags } from '../selectedStyles';

const customStyles = {
    dropdownIndicator: (provided) => ({
        ...provided,
        display: 'none'
    }),
    indicatorSeparator: () => ({
        display: 'none', // Hide the separator if not needed
    }),
    container: (provided) => ({
        ...provided,
        flex: '1 1 160px',
        minWidth: '150px',
        maxWidth: '260px',
    }),
    control: (provided, state) => ({
        ...provided,
        borderRadius: 'var(--radius-pill)',
        boxShadow: state.isFocused ? '0 0 0 3px var(--color-primary-soft)' : 'none',
        backgroundColor: 'var(--color-surface)',
        borderColor: state.isFocused ? 'var(--color-primary)' : 'var(--color-border-strong)',
        minHeight: '44px',
        padding: '0 6px',
        '&:hover': { borderColor: 'var(--color-primary)' },
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        color: 'var(--color-text-primary)',
    }),
    placeholder: (provided) => ({
        ...provided,
        margin: 0,
        color: 'var(--color-text-muted)',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: 'var(--color-text-primary)',
    }),
    menu: (provided) => ({
        ...provided,
        width: 'auto',
        minWidth: '100%',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border-strong)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        zIndex: 20,
    }),
    menuList: (provided) => ({
        ...provided,
        width: 'auto',
    }),
};

const SearchBar = ({ loggedInUser, openModal, albumsPass = [], collectionsPass = [], tagsPass = [], cardsPass = [], userPass = [], allCardsPass = [], usersPass = [] }) => {
    const navigate = useNavigate();
    const [selectedAlbums, setSelectedAlbums] = useState(albumsPass);
    const [selectedCollections, setSelectedCollections] = useState(collectionsPass);
    const [selectedTags, setSelectedTags] = useState(tagsPass);
    const [cards, setCards] = useState(allCardsPass);
    const [users, setUsers] = useState(usersPass);
    const [selectedCards, setSelectedCards] = useState(cardsPass);
    const selectedUser = userPass;
    const [searchType, setSearchType] = useState('decks'); // Default to 'decks'
    const sortBy = "score";
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fontSize = viewportWidth > 768 ? '16px' : '12px';

    const handleDeckSearch = () => {
        const searchParams = {
            selectedAlbums,
            selectedCollections,
            selectedTags,
            selectedCards,
            selectedUser,
            cards,
            users,
            sortBy
        };
        navigate('/deck-search-results', { state: { searchParams } });
    };


    const renameKey = (obj, oldKey, newKey) => {
        if (oldKey in obj) {
            obj[newKey] = obj[oldKey];
            delete obj[oldKey];
        }
        return obj;
    };

    const fetchCards = useCallback(async () => {
        try {
            const response = await fetch(`${ROUTE}/api/cards/`);
            if (!response.ok) {
                throw new Error('Failed to fetch cards');
            }
            const cardsData = await response.json();
            const renamedData = cardsData.map(item => renameKey(item, 'Name', 'label')).map(item => renameKey(item, 'Code', 'value'));
            setCards(renamedData);
        } catch (error) {
            console.error('Error fetching cards:', error);
        }
    }, []); // Dependency array is empty assuming no external dependencies

    useEffect(() => {
        fetchCards();
    }, [fetchCards]); // useEffect dependency on fetchCards function

    const fetchUsers = useCallback(async () => {
        try {
            const response = await fetch(`${ROUTE}/api/users/getall/`);
            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }
            const userData = await response.json();
            const renamedData = userData.map(item => renameKey(item, 'username', 'label'));
            renamedData['value'] = renamedData['label'];
            setUsers(userData);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return (
        <div className="search-bar-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: fontSize }}>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
                {loggedInUser && loggedInUser.email && (
                    <button onClick={openModal} className='modern-button'>
                        Upload a deck
                    </button>
                )}
                <button onClick={() => setSearchType('decks')} className="modern-button">
                    Search for Decks
                </button>
            </div>


            <div className="filter-row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', justifyContent: 'center', gap: '8px', width: '100%', maxWidth: '1000px' }}>
                {searchType === 'decks' && (
                    <>
                        <Select
                            isMulti
                            options={optionsAlbums}
                            value={selectedAlbums}
                            onChange={setSelectedAlbums}
                            placeholder="Albums"
                            styles={{ ...customStyles, ...customStylesAlbums }}
                        />
                        <Select
                            isMulti
                            options={optionsCollections}
                            value={selectedCollections}
                            onChange={setSelectedCollections}
                            placeholder="Collections"
                            styles={{ ...customStyles, ...customStylesCollections }}
                        />
                        <Select
                            isMulti
                            options={optionsTags}
                            value={selectedTags}
                            onChange={setSelectedTags}
                            placeholder="Tags"
                            styles={{ ...customStyles, ...customStylesTags }}
                        />
                        <Select
                            isMulti
                            options={cards}
                            value={selectedCards}
                            onChange={setSelectedCards}
                            placeholder="Cards"
                            styles={{ ...customStyles, ...customStylesCards }}
                        />
                    </>
                )}

                {searchType === 'decks' && (
                    <>
                        <button onClick={handleDeckSearch} style={{ marginLeft: '5px', padding: '12px' }} className='modern-button'>
                            <FontAwesomeIcon icon={faSearch} />
                        </button>
                    </>
                )}
            </div>

        </div>
    );
};

export default SearchBar;